import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import Button from '@components/ui/Button';
import DatePicker from '@components/ui/DatePicker';
import { useApiClient } from '@hooks/useApiClient';
import { toast } from '@utils/toast';
import { API_CONFIG } from '../../../../constants/api.constants';
import {
    rebuildClosingStock,
    updateTodayStockCount,
} from '../../../../services/storeStockOpsService';
import type {
    RebuildClosingStockResponse,
    StoreSearchResult,
} from '../../../../types/api.types';
import { enumerateInclusiveYmd, formatIstYmd, startOfLocalToday } from '../../../../utils/dateOnly';
import ConfirmStockOpsDialog from './ConfirmStockOpsDialog';

interface StockOpsTabProps {
    store: StoreSearchResult;
    storeBrandId: string;
    isRunning: boolean;
    setIsRunning: (v: boolean) => void;
}

type ConfirmKind = 'rebuild' | 'updateToday';

interface RebuildResult {
    ok: boolean;
    text: string;
}

const NO_RESPONSE_MESSAGE = 'No response from server. Please check your internet connection.';

function requestErrorMessage(apiMessage: string): string {
    if (apiMessage === NO_RESPONSE_MESSAGE) {
        return 'Timeout or network error';
    }
    return apiMessage || 'Request failed';
}

function validateRange(from: Date | undefined, to: Date | undefined): string | null {
    if (!from || !to) {
        return 'Select both From (IST) and To (IST) dates.';
    }

    const fromYmd = formatIstYmd(from);
    const toYmd = formatIstYmd(to);
    const todayYmd = formatIstYmd(new Date());

    if (fromYmd > todayYmd || toYmd > todayYmd) {
        return 'Dates cannot be after today (IST).';
    }

    if (fromYmd > toYmd) {
        return 'From date must be on or before To date.';
    }

    const days = enumerateInclusiveYmd(fromYmd, toYmd);
    if (days.length > API_CONFIG.STOCK_OPS_MAX_INCLUSIVE_DAYS) {
        return `Range cannot exceed ${API_CONFIG.STOCK_OPS_MAX_INCLUSIVE_DAYS} days.`;
    }

    return null;
}

function formatRebuildSummary(data: RebuildClosingStockResponse): string {
    const dayWord = data.daysProcessed === 1 ? 'day' : 'days';
    return `${data.fromDate} to ${data.toDate} (IST) · ${data.daysProcessed} ${dayWord} · ${data.totalProcessed} processed`;
}

export default function StockOpsTab({
    store,
    storeBrandId,
    isRunning,
    setIsRunning,
}: StockOpsTabProps) {
    const { callApi } = useApiClient({ toast: false });
    const inFlightRef = useRef(false);

    const [fromDate, setFromDate] = useState<Date | undefined>();
    const [toDate, setToDate] = useState<Date | undefined>();
    const [rebuildResult, setRebuildResult] = useState<RebuildResult | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);
    const [updateTodayResult, setUpdateTodayResult] = useState<string | null>(null);
    const [rangeError, setRangeError] = useState<string | null>(null);

    const isRangeValid = validateRange(fromDate, toDate) === null;
    const isLiveStore = store.storeStatus === 'Active';
    const pickerMaxDate = startOfLocalToday();
    const fromYmd = fromDate ? formatIstYmd(fromDate) : '';
    const toYmd = toDate ? formatIstYmd(toDate) : '';
    const rebuildDayCount =
        fromDate && toDate && fromYmd <= toYmd ? enumerateInclusiveYmd(fromYmd, toYmd).length : 0;
    const confirmTitle =
        confirmKind === 'updateToday' ? "Update today's stock count" : 'Rebuild closing stock';
    const confirmLabel =
        confirmKind === 'updateToday' ? "Update today's stock count" : 'Rebuild closing stock';
    const pickerClassName = isRunning ? 'pointer-events-none opacity-50' : '';

    let confirmBody: ReactNode = null;
    if (confirmKind === 'rebuild') {
        confirmBody = (
            <>
                <p>
                    Store: <span className="font-medium text-[#1e293b]">{store.name}</span>
                </p>
                <p>
                    From {fromYmd} to {toYmd} (IST) — {rebuildDayCount} day
                    {rebuildDayCount === 1 ? '' : 's'} in one request.
                </p>
                <p>This overwrites existing closing snapshots in the range.</p>
            </>
        );
    } else if (confirmKind === 'updateToday') {
        confirmBody = (
            <>
                <p>
                    Store: <span className="font-medium text-[#1e293b]">{store.name}</span>
                </p>
                <p>
                    Sets today&apos;s live POS quantity from yesterday&apos;s closing + today&apos;s
                    movements, including in-store losses. Does not write StoreStockDelta.
                </p>
            </>
        );
    }

    const handleFromChange = useCallback(
        (date: Date | undefined) => {
            if (isRunning) return;
            setFromDate(date);
            setRangeError(date && toDate ? validateRange(date, toDate) : null);
        },
        [isRunning, toDate]
    );

    const handleToChange = useCallback(
        (date: Date | undefined) => {
            if (isRunning) return;
            setToDate(date);
            setRangeError(fromDate && date ? validateRange(fromDate, date) : null);
        },
        [isRunning, fromDate]
    );

    const handleCancelConfirm = useCallback(() => {
        if (isRunning) return;
        setConfirmKind(null);
    }, [isRunning]);

    const handleRebuildClick = useCallback(() => {
        if (isRunning || inFlightRef.current) return;
        const error = validateRange(fromDate, toDate);
        if (error) {
            setRangeError(error);
            return;
        }
        setRangeError(null);
        setConfirmKind('rebuild');
    }, [isRunning, fromDate, toDate]);

    const handleUpdateTodayClick = useCallback(() => {
        if (isRunning || inFlightRef.current) return;
        setConfirmKind('updateToday');
    }, [isRunning]);

    const handleConfirmRebuild = useCallback(async () => {
        if (!fromDate || !toDate) return;
        if (isRunning || inFlightRef.current) return;

        const rangeFromYmd = formatIstYmd(fromDate);
        const rangeToYmd = formatIstYmd(toDate);

        inFlightRef.current = true;
        setIsRunning(true);
        setRebuildResult(null);

        let apiMessage = 'Request failed';
        try {
            const result = await callApi(
                () =>
                    rebuildClosingStock(store._id, storeBrandId, rangeFromYmd, rangeToYmd),
                undefined,
                (message: string) => {
                    apiMessage = message;
                }
            );

            setConfirmKind(null);

            if (result === null) {
                const message = requestErrorMessage(apiMessage);
                setRebuildResult({ ok: false, text: message });
                toast.error(message);
                return;
            }

            const text = formatRebuildSummary(result.data);
            setRebuildResult({ ok: true, text });
            toast.success(text);
        } finally {
            inFlightRef.current = false;
            setIsRunning(false);
        }
    }, [fromDate, toDate, isRunning, callApi, store._id, storeBrandId, setIsRunning]);

    const handleConfirmUpdateToday = useCallback(async () => {
        if (isRunning || inFlightRef.current) return;
        inFlightRef.current = true;
        setIsRunning(true);

        let apiMessage = 'Request failed';
        try {
            const result = await callApi(
                () => updateTodayStockCount(store._id, storeBrandId),
                undefined,
                (message: string) => {
                    apiMessage = message;
                }
            );

            setConfirmKind(null);

            if (result === null) {
                toast.error(requestErrorMessage(apiMessage));
                return;
            }

            const summary = result.data.summary;
            const date = result.data.date;
            setUpdateTodayResult(
                `Updated ${summary.productsUpdated} product${summary.productsUpdated === 1 ? '' : 's'} on ${date} (${summary.productsAlreadyCorrect} already correct).`
            );
            toast.success(
                `Updated ${summary.productsUpdated} product${summary.productsUpdated === 1 ? '' : 's'} for ${date}.`
            );
        } finally {
            inFlightRef.current = false;
            setIsRunning(false);
        }
    }, [isRunning, callApi, store._id, storeBrandId, setIsRunning]);

    const handleConfirm = confirmKind === 'updateToday' ? handleConfirmUpdateToday : handleConfirmRebuild;

    useEffect(() => {
        if (!isRunning) return;

        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isRunning]);

    return (
        <div className="space-y-6">
            <section className="rounded-xl border border-[#e2e8f0] p-4 space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-[#1e293b]">Rebuild closing stock</h3>
                    <p className="text-xs text-[#64748b] mt-1">
                        Rewrites Stock History and reports for the selected IST range in one request.
                        Does not change live POS quantity. Prefer after store close.
                    </p>
                </div>

                <div className={`grid grid-cols-2 gap-4 ${pickerClassName}`}>
                    <DatePicker
                        label="From (IST)"
                        value={fromDate}
                        onChange={handleFromChange}
                        placeholder="Select start date"
                        maxDate={pickerMaxDate}
                        error={rangeError ?? undefined}
                    />
                    <DatePicker
                        label="To (IST)"
                        value={toDate}
                        onChange={handleToChange}
                        placeholder="Select end date"
                        maxDate={pickerMaxDate}
                    />
                </div>

                <Button
                    variant="primary"
                    className="w-full"
                    isLoading={isRunning}
                    disabled={isRunning || !isRangeValid}
                    onClick={handleRebuildClick}
                >
                    Rebuild closing stock
                </Button>

                {rebuildResult ? (
                    <p
                        className={`text-sm rounded-lg px-3 py-2 border ${
                            rebuildResult.ok
                                ? 'text-[#15803d] bg-[#f0fdf4] border-[#bbf7d0]'
                                : 'text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]'
                        }`}
                    >
                        {rebuildResult.text}
                    </p>
                ) : null}
            </section>

            <section className="rounded-xl border border-[#e2e8f0] p-4 space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-[#1e293b]">
                        Update today&apos;s stock count
                    </h3>
                    <p className="text-xs text-[#64748b] mt-1">
                        Sets live POS quantity now. Does not write StoreStockDelta. Applies in-store
                        losses (same as rebuild).
                    </p>
                </div>

                {isLiveStore ? (
                    <p className="text-sm text-[#92400e] bg-[#fffbeb] border border-[#fde68a] rounded-lg px-3 py-2">
                        This store is Live — POS may be selling.
                    </p>
                ) : null}

                <Button
                    variant="primary"
                    className="w-full"
                    isLoading={isRunning}
                    disabled={isRunning}
                    onClick={handleUpdateTodayClick}
                >
                    Update today&apos;s stock count
                </Button>

                {updateTodayResult ? (
                    <p className="text-sm text-[#15803d] bg-[#f0fdf4] border border-[#bbf7d0] rounded-lg px-3 py-2">
                        {updateTodayResult}
                    </p>
                ) : null}
            </section>

            <ConfirmStockOpsDialog
                isOpen={confirmKind !== null}
                storeName={store.name}
                title={confirmTitle}
                body={confirmBody}
                confirmLabel={confirmLabel}
                isLoading={isRunning}
                onCancel={handleCancelConfirm}
                onConfirm={handleConfirm}
            />
        </div>
    );
}
